import { useState, useMemo } from "react"
import { useDispatch } from "react-redux"
import { StoreDispatch } from "../../store/configure-store"
import { FromDropdown } from "../../cmps/forms/input/form-dropdown"
import { FormInputText } from "../../cmps/forms/input/form-input-text"
import { RequiredAsterisk } from "../../cmps/forms/required-asterisk"
import { Backlog } from "../../cmps/util-cmps/backlog"
import { Spinner } from "../../cmps/util-cmps/spinner"
import { UserMsg } from "../../models/user"
import { setUserMsg } from "../../store/user"
import { httpService } from "../../services/http-service"
import { updateObjectiveMutation } from "../../cmps/util-cmps/graphql-mutations/update-objective"
import { useEscapeKey } from "../../hooks/use-escape-key"
import { useScrollLock } from "../../hooks/use-scroll-lock"
import { useFocusTrap } from "../../hooks/use-focus-trap"
import { SystemTypes, getRequiredSystems, setRequiredSystems } from "../forms/is-required-system"

interface ObjectiveFormModalProps {
    objectives: any[]
    objectiveToEdit?: any
    onSuccess: () => void
    onClose: () => void
}

const getDistinctValues = (objectives: any[], field: string): string[] =>
    Array.from(new Set(objectives.map((o: any) => o[field]).filter(Boolean) as string[])).sort()

const getInitials = (name: string): string =>
    name.split(/\s+/).map(w => w[0]?.toUpperCase() || '').join('')

const getNextObjectiveNumber = (objectives: any[]): number => {
    const nums = objectives.map((o: any) => o.objectiveNumber).filter((n: any) => typeof n === 'number' && !isNaN(n))
    return nums.length > 0 ? Math.max(...nums) + 1 : 1
}

const getEmptyForm = (objectives: any[]) => ({
    name: '',
    objectiveNumber: String(getNextObjectiveNumber(objectives)),
    domain: '',
    domainAbbreviation: '',
    description: '',
    risk: '',
    frequency: '',
    type: '',
    itDependency: '',
    testType: '',
    specification: '',
    populationType: '',
    failureCriteria: '',
    testPlan: '',
    abbreviation: '',
})

const getFormFromObjective = (obj: any) => ({
    name: obj.name || '',
    objectiveNumber: String(obj.objectiveNumber ?? ''),
    domain: obj.domain || '',
    domainAbbreviation: obj.domainAbbreviation || '',
    description: obj.description || '',
    risk: obj.risk || '',
    frequency: obj.frequency || '',
    type: obj.type || '',
    itDependency: obj.ITDependency || '',
    testType: obj.testType || '',
    specification: obj.specification || '',
    populationType: obj.populationType || '',
    failureCriteria: obj.failureCriteria || '',
    testPlan: obj.testPlan || '',
    abbreviation: obj.abbreviation || '',
})

const ENV_NAME = (process.env.REACT_APP_BACKEND_ENVIRONMENT_NAME || '').toLowerCase()
const CROSS_REGION_LABEL: Record<string, string> = {
    prod: 'Prod US',
    produs: 'Prod EU',
}

export const ObjectiveFormModal: React.FC<ObjectiveFormModalProps> = ({ objectives, objectiveToEdit, onSuccess, onClose }) => {
    const isEditMode = !!objectiveToEdit
    const otherRegion = CROSS_REGION_LABEL[ENV_NAME]
    const dispatch = useDispatch<StoreDispatch>()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [form, setForm] = useState(() => isEditMode ? getFormFromObjective(objectiveToEdit) : getEmptyForm(objectives))

    const objNum = parseInt(form.objectiveNumber, 10)
    const [requiredSystems, setRequiredSystemsState] = useState<string[]>(() => {
        if (isEditMode && !isNaN(objNum)) return getRequiredSystems(objNum)
        return [SystemTypes.IN_SCOPE]
    })
    const toggleRequiredSystem = (systemType: string) => {
        setRequiredSystemsState(prev => {
            if (systemType === SystemTypes.IN_SCOPE) return prev // Main is always required
            return prev.includes(systemType) ? prev.filter(s => s !== systemType) : [...prev, systemType]
        })
    }

    useEscapeKey(onClose)
    useScrollLock()
    const trapRef = useFocusTrap<HTMLDivElement>()

    const domainOptions = useMemo(() => getDistinctValues(objectives, 'domain').map(v => ({ id: v, name: v })), [objectives])
    const frequencyOptions = useMemo(() => getDistinctValues(objectives, 'frequency').map(v => ({ id: v, name: v })), [objectives])
    const typeOptions = useMemo(() => getDistinctValues(objectives, 'type').map(v => ({ id: v, name: v })), [objectives])
    const itDependencyOptions = useMemo(() => getDistinctValues(objectives, 'ITDependency').map(v => ({ id: v, name: v })), [objectives])
    const testTypeOptions = useMemo(() => getDistinctValues(objectives, 'testType').map(v => ({ id: v, name: v })), [objectives])
    const specificationOptions = useMemo(() => getDistinctValues(objectives, 'specification').map(v => ({ id: v, name: v })), [objectives])
    const populationTypeOptions = useMemo(() => getDistinctValues(objectives, 'populationType').map(v => ({ id: v, name: v })), [objectives])

    const domainAbbreviationOptions = useMemo(() => {
        if (!form.domain) return []
        const abbrs = Array.from(new Set(
            objectives
                .filter((o: any) => o.domain === form.domain && o.domainAbbreviation)
                .map((o: any) => o.domainAbbreviation as string)
        )).sort()
        return abbrs.map(v => ({ id: v, name: v }))
    }, [objectives, form.domain])

    const handleDomainChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const domain = e.target.value
        const abbrs = Array.from(new Set(
            objectives
                .filter((o: any) => o.domain === domain && o.domainAbbreviation)
                .map((o: any) => o.domainAbbreviation as string)
        ))
        setForm(prev => ({
            ...prev,
            domain,
            domainAbbreviation: abbrs.length === 1 ? abbrs[0] : ''
        }))
    }

    const onChangeText = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target
        if (name === 'name') {
            const initials = getInitials(value)
            setForm(prev => prev.abbreviation === getInitials(prev.name)
                ? { ...prev, name: value, abbreviation: initials }
                : { ...prev, name: value }
            )
            return
        }
        setForm(prev => ({ ...prev, [name]: value }))
    }

    const onChangeDropdown = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target
        setForm(prev => ({ ...prev, [name]: value }))
    }

    const existingObjNumbers = useMemo(() => new Set(objectives.map((o: any) => o.objectiveNumber)), [objectives])
    const existingAbbreviations = useMemo(() => new Set(objectives.map((o: any) => o.abbreviation?.toLowerCase()).filter(Boolean)), [objectives])

    const validate = (): string | null => {
        const trimmed = {
            name: form.name.trim(),
            objectiveNumber: form.objectiveNumber.trim(),
            description: form.description.trim(),
            risk: form.risk.trim(),
            testPlan: form.testPlan.trim(),
            failureCriteria: form.failureCriteria.trim(),
            abbreviation: form.abbreviation.trim(),
            domainAbbreviation: form.domainAbbreviation.trim(),
        }

        if (!trimmed.name) return 'Name is required'
        if (trimmed.name.length > 200) return 'Name exceeds 200 characters'

        const objNum = parseInt(trimmed.objectiveNumber, 10)
        if (!trimmed.objectiveNumber || isNaN(objNum) || objNum <= 0) return 'Objective Number must be a positive integer'
        const objNumChanged = !isEditMode || objNum !== objectiveToEdit.objectiveNumber
        if (objNumChanged && existingObjNumbers.has(objNum)) return `Objective Number ${objNum} already exists`

        if (!form.domain) return 'Domain is required'
        if (!trimmed.domainAbbreviation) return 'Domain Abbreviation is required'
        if (!trimmed.description) return 'Description is required'
        if (trimmed.description.length > 2000) return 'Description exceeds 2000 characters'
        if (!trimmed.risk) return 'Risk is required'
        if (trimmed.risk.length > 2000) return 'Risk exceeds 2000 characters'
        if (!form.frequency) return 'Frequency is required'
        if (!form.type) return 'Type is required'
        if (!form.itDependency) return 'IT Dependency is required'
        if (!form.testType) return 'Test Type is required'
        if (!form.specification) return 'Specification is required'
        if (!form.populationType) return 'Population Type is required'
        if (!trimmed.failureCriteria) return 'Failure Criteria is required'
        if (trimmed.failureCriteria.length > 2000) return 'Failure Criteria exceeds 2000 characters'
        if (!trimmed.testPlan) return 'Test Plan is required'
        if (trimmed.testPlan.length > 2000) return 'Test Plan exceeds 2000 characters'
        if (!trimmed.abbreviation) return 'Abbreviation is required'
        if (trimmed.abbreviation.length > 20) return 'Abbreviation exceeds 20 characters'
        if (!/^[A-Za-z0-9_-]+$/.test(trimmed.abbreviation)) return 'Abbreviation must be alphanumeric with hyphens/underscores only'
        const abbrChanged = !isEditMode || trimmed.abbreviation.toLowerCase() !== (objectiveToEdit.abbreviation || '').toLowerCase()
        if (abbrChanged && existingAbbreviations.has(trimmed.abbreviation.toLowerCase())) return `Abbreviation "${trimmed.abbreviation}" already exists`

        return null
    }

    const onSubmit = async () => {
        const error = validate()
        if (error) {
            const msg: UserMsg = { type: 'danger', txt: error }
            dispatch(setUserMsg({ msg }))
            return
        }

        setIsSubmitting(true)
        try {
            const parsedObjNum = parseInt(form.objectiveNumber, 10)
            if (isEditMode) {
                const query = updateObjectiveMutation()
                const variables = {
                    objectiveId: Number(objectiveToEdit.id),
                    name: form.name.trim(),
                    objectiveNumber: parsedObjNum,
                    domain: form.domain,
                    domainAbbreviation: form.domainAbbreviation.trim(),
                    description: form.description.trim(),
                    risk: form.risk.trim(),
                    frequency: form.frequency,
                    type: form.type,
                    itDependency: form.itDependency,
                    testType: form.testType,
                    specification: form.specification,
                    populationType: form.populationType,
                    failureCriteria: form.failureCriteria.trim(),
                    testPlan: form.testPlan.trim(),
                    abbreviation: form.abbreviation.trim(),
                }
                await httpService.graphql('auditech', { query, variables })
                setRequiredSystems(parsedObjNum, requiredSystems)
                const msg: UserMsg = { type: 'success', txt: 'Objective updated successfully' }
                dispatch(setUserMsg({ msg }))
                onSuccess()
            } else {
                const payload = {
                    name: form.name.trim(),
                    objective_number: parseInt(form.objectiveNumber, 10),
                    domain: form.domain,
                    domain_abbreviation: form.domainAbbreviation.trim(),
                    description: form.description.trim(),
                    risk: form.risk.trim(),
                    frequency: form.frequency,
                    type: form.type,
                    IT_dependency: form.itDependency,
                    test_type: form.testType,
                    specification: form.specification,
                    population_type: form.populationType,
                    failure_criteria: form.failureCriteria.trim(),
                    test_plan: form.testPlan.trim(),
                    abbreviation: form.abbreviation.trim(),
                }
                const res = await httpService.post('create-objective-all-dbs', payload)
                const allSuccess = res.results?.every((r: any) => r.success)
                if (allSuccess) {
                    setRequiredSystems(parsedObjNum, requiredSystems)
                    const msg: UserMsg = { type: 'success', txt: 'Objective created successfully' }
                    dispatch(setUserMsg({ msg }))
                    onSuccess()
                } else {
                    const failed = res.results?.filter((r: any) => !r.success).map((r: any) => r.company).join(', ')
                    const msg: UserMsg = { type: 'danger', txt: `Objective creation failed for: ${failed}` }
                    dispatch(setUserMsg({ msg }))
                }
            }
        } catch {
            const msg: UserMsg = { type: 'danger', txt: isEditMode ? 'Failed updating objective' : 'Failed creating objective' }
            dispatch(setUserMsg({ msg }))
        } finally {
            setIsSubmitting(false)
        }
    }

    const modalTitle = isEditMode ? 'Edit Objective' : 'Create Objective'

    return <div ref={trapRef}>
        <section className="objective-form-modal" role="dialog" aria-modal="true" aria-label={modalTitle}>
            <header>
                <h2>{modalTitle}</h2>
                <button className="close-btn" onClick={onClose} aria-label="Close">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </header>

            {otherRegion && (
                <div className="cross-region-note">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>Remember to also create this objective in <strong>{otherRegion}</strong></span>
                </div>
            )}

            <section className="modal-form-body">
                <div className="form-section-card">
                    <div className="form-section-label">General</div>
                    <div className="form-row">
                        <div className="field">
                            <FormInputText
                                keyName="name"
                                label="Name"
                                isRequired={true}
                                value={form.name}
                                onChange={onChangeText}
                            />
                        </div>
                        <div className="field">
                            <FormInputText
                                keyName="objectiveNumber"
                                label="Objective Number"
                                isRequired={true}
                                isNumberInput={true}
                                value={form.objectiveNumber}
                                onChange={onChangeText}
                            />
                        </div>
                    </div>
                </div>

                <div className="form-section-card">
                    <div className="form-section-label">Domain</div>
                    <div className="form-row">
                        <div className="field">
                            <FromDropdown
                                keyName="domain"
                                label="Domain"
                                options={domainOptions}
                                value={form.domain}
                                onSelectValue={handleDomainChange}
                            />
                        </div>
                        <div className="field">
                            <FromDropdown
                                keyName="domainAbbreviation"
                                label="Domain Abbreviation"
                                options={domainAbbreviationOptions}
                                value={form.domainAbbreviation}
                                onSelectValue={onChangeDropdown}
                                hideBlankOption={domainAbbreviationOptions.length > 0}
                            />
                        </div>
                    </div>
                </div>

                <div className="form-section-card">
                    <div className="form-section-label">Description</div>
                    <div className="textarea-field">
                        <label htmlFor="description">Description <RequiredAsterisk /></label>
                        <textarea
                            name="description"
                            id="description"
                            className="form-textarea"
                            value={form.description}
                            onChange={onChangeText}
                            rows={3}
                            maxLength={2000}
                        />
                    </div>
                    <div className="textarea-field">
                        <label htmlFor="risk">Risk <RequiredAsterisk /></label>
                        <textarea
                            name="risk"
                            id="risk"
                            className="form-textarea"
                            value={form.risk}
                            onChange={onChangeText}
                            rows={3}
                            maxLength={2000}
                        />
                    </div>
                </div>

                <div className="form-section-card">
                    <div className="form-section-label">Classification</div>
                    <div className="form-row three-col">
                        <div className="field">
                            <FromDropdown
                                keyName="frequency"
                                label="Frequency"
                                options={frequencyOptions}
                                value={form.frequency}
                                onSelectValue={onChangeDropdown}
                            />
                        </div>
                        <div className="field">
                            <FromDropdown
                                keyName="type"
                                label="Type"
                                options={typeOptions}
                                value={form.type}
                                onSelectValue={onChangeDropdown}
                            />
                        </div>
                        <div className="field">
                            <FromDropdown
                                keyName="itDependency"
                                label="IT Dependency"
                                options={itDependencyOptions}
                                value={form.itDependency}
                                onSelectValue={onChangeDropdown}
                            />
                        </div>
                    </div>
                    <div className="form-row three-col">
                        <div className="field">
                            <FromDropdown
                                keyName="testType"
                                label="Test Type"
                                options={testTypeOptions}
                                value={form.testType}
                                onSelectValue={onChangeDropdown}
                            />
                        </div>
                        <div className="field">
                            <FromDropdown
                                keyName="specification"
                                label="Specification"
                                options={specificationOptions}
                                value={form.specification}
                                onSelectValue={onChangeDropdown}
                            />
                        </div>
                        <div className="field">
                            <FromDropdown
                                keyName="populationType"
                                label="Population Type"
                                options={populationTypeOptions}
                                value={form.populationType}
                                onSelectValue={onChangeDropdown}
                            />
                        </div>
                    </div>
                </div>

                <div className="form-section-card">
                    <div className="form-section-label">Criteria</div>
                    <div className="textarea-field">
                        <label htmlFor="failureCriteria">Failure Criteria <RequiredAsterisk /></label>
                        <textarea
                            name="failureCriteria"
                            id="failureCriteria"
                            className="form-textarea"
                            value={form.failureCriteria}
                            onChange={onChangeText}
                            rows={3}
                            maxLength={2000}
                        />
                    </div>
                    <div className="textarea-field">
                        <label htmlFor="testPlan">Test Plan <RequiredAsterisk /></label>
                        <textarea
                            name="testPlan"
                            id="testPlan"
                            className="form-textarea"
                            value={form.testPlan}
                            onChange={onChangeText}
                            rows={3}
                            maxLength={2000}
                        />
                    </div>
                </div>

                <div className="form-section-card">
                    <div className="form-section-label">Identifier</div>
                    <div className="form-row">
                        <div className="field">
                            <FormInputText
                                keyName="abbreviation"
                                label="Abbreviation"
                                isRequired={true}
                                value={form.abbreviation}
                                onChange={onChangeText}
                            />
                        </div>
                    </div>
                </div>

                <div className="form-section-card">
                    <div className="form-section-label">Required Systems</div>
                    <p className="form-info">Select which system types are mandatory when creating audit controls for this objective.</p>
                    <div className="required-systems-checkboxes">
                        <label className="system-checkbox">
                            <input type="checkbox" checked={true} disabled />
                            <span>Main System (In Scope)</span>
                        </label>
                        <label className="system-checkbox">
                            <input type="checkbox" checked={requiredSystems.includes(SystemTypes.HR)} onChange={() => toggleRequiredSystem(SystemTypes.HR)} />
                            <span>HR System</span>
                        </label>
                        <label className="system-checkbox">
                            <input type="checkbox" checked={requiredSystems.includes(SystemTypes.TICKET)} onChange={() => toggleRequiredSystem(SystemTypes.TICKET)} />
                            <span>Ticket System</span>
                        </label>
                    </div>
                </div>

                <div className="form-actions">
                    {!isSubmitting && <button className="main" onClick={onSubmit} disabled={isSubmitting}>
                        {isEditMode ? 'Save' : 'Create'}
                    </button>}
                    {isSubmitting && <span className="spinner"><Spinner isSmall={true} /></span>}
                </div>
            </section>
        </section>
        <Backlog zIndex={199} onCloseBacklog={onClose} />
    </div>
}
